'use client';
import React, { useEffect, useState } from 'react';
import { Trophy, Zap, Clock, Target, Users, Activity } from 'lucide-react';

interface WeeklyEntry {
  score: number;
  peakSize: number;
  survivalTime: number;
  dotsCollected: number;
  absorbed: number;
}

const MOCK_WEEKLY_STATS = {
  topScore: 284750,
  topScoreHolder: 'APEX-NX7',
  totalSessions: 8421,
  avgSurvivalTime: 847,
  peakSize: 1240,
  totalDotsConsumed: 4820000,
};

export default function WeeklyStatsCards() {
  const [personalBest, setPersonalBest] = useState<number | null>(null);
  const [personalRank, setPersonalRank] = useState<number | null>(null);

  useEffect(() => {
    // Read personal best from localStorage
    try {
      const weekly = JSON.parse(localStorage.getItem('lunora_weekly') || '[]') as WeeklyEntry[];
      if (weekly.length > 0) {
        const best = Math.max(...weekly.map((e) => e.score));
        setPersonalBest(best);
        // Mock rank position (would come from backend)
        setPersonalRank(Math.floor(Math.random() * 50) + 1);
      }
    } catch {
      // ignore
    }
  }, []);

  const cards = [
    {
      id: 'wstat-top',
      icon: Trophy,
      label: 'Top Score This Week',
      value: MOCK_WEEKLY_STATS.topScore.toLocaleString(),
      sub: `by ${MOCK_WEEKLY_STATS.topScoreHolder}`,
      color: 'text-yellow-400',
      highlight: true,
    },
    {
      id: 'wstat-sessions',
      icon: Users,
      label: 'Total Sessions',
      value: MOCK_WEEKLY_STATS.totalSessions.toLocaleString(),
      sub: 'This week',
      color: 'text-primary',
    },
    {
      id: 'wstat-survival',
      icon: Clock,
      label: 'Avg Survival Time',
      value: `${Math.floor(MOCK_WEEKLY_STATS.avgSurvivalTime / 60)}m ${MOCK_WEEKLY_STATS.avgSurvivalTime % 60}s`,
      sub: 'All sessions',
      color: 'text-neon-green',
    },
    {
      id: 'wstat-size',
      icon: Target,
      label: 'Peak Size Reached',
      value: MOCK_WEEKLY_STATS.peakSize.toString(),
      sub: 'Largest organism this week',
      color: 'text-neon-orange',
    },
    {
      id: 'wstat-dots',
      icon: Zap,
      label: 'Total Dots Consumed',
      value: `${(MOCK_WEEKLY_STATS.totalDotsConsumed / 1000000).toFixed(2)}M`,
      sub: 'Arena-wide this week',
      color: 'text-neon-cyan',
    },
    {
      id: 'wstat-personal',
      icon: Activity,
      label: 'Your Personal Best',
      value: personalBest ? personalBest.toLocaleString() : '—',
      sub: personalRank ? `Rank #${personalRank} this week` : 'No sessions yet',
      color: 'text-neon-magenta',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map(card => (
        <div
          key={card.id}
          className={`stat-card ${card.highlight ? 'border-yellow-400/30 bg-yellow-400/5' : ''}`}
        >
          <card.icon size={16} className={`${card.color} mb-3`} />
          <div className={`text-xl font-bold font-mono ${card.color} mb-0.5`}>{card.value}</div>
          <div className="text-xs font-medium text-foreground mb-1">{card.label}</div>
          <div className="text-xs text-muted-foreground">{card.sub}</div>
        </div>
      ))}
    </div>
  );
}